import { useCallback, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v7 as uuid } from 'uuid';
import { getDayName } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { ResourcePanel } from '../../../projectManager/ResourcePanel';
import { FormCircularProgress, FormContext, Input } from '../../../common/forms/FormElements';
import { ApiRoutes, ChargeTypeResourceTypes } from '../../../Common';

const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const resourceHasAnyAssignments = (resource) =>
    Boolean(resource && dayKeys.some((day) => Number(resource[day]) > 0));

const selectResourceKits = (data) => (data || []).map((rk) => ({
        value: rk.id,
        label: rk.name,
        tooltip: rk.description,
        resources: rk.resources,
    }));

const selectResources = (data) => (data || []).map((r) => ({
        ...r,
        id: !r.id ? uuid() : r.id,
    }));

export const Resources = () => {
    const { values: job, setValues } = useContext(FormContext);

    const { data: resourceKits, isLoading: resourceKitsLoading } = useQuery({
        queryKey: ['resourceKits'],
        queryFn: () => httpClient.js(ApiRoutes.resourceKit.byTenant()),
        select: selectResourceKits,
    });

    const { data: resources, isLoading: resourcesLoading } = useQuery({
        queryKey: ['contractResources', job.contractId],
        queryFn: () => httpClient.js(ApiRoutes.job.getResources(job.contractId)),
        select: selectResources,
    });

    const { data: contract, isLoading: contractLoading } = useQuery({
        queryKey: ['contract', job.contractId],
        queryFn: () => httpClient.js(ApiRoutes.contract.byId(job.contractId)),
    });

    const isRentalContract = Boolean(contract?.isProjectContract);

    const handleResourceUpdate = useCallback((updatedJob) => {
        const startDayName = getDayName(job.startTime);
        const jobResources = updatedJob?.resources ?? [];
        const resourcesSet = jobResources.some((resource) => resource[startDayName] > 0);

        const hasAssignedResources = jobResources.some((r) => resourceHasAnyAssignments(r));
        const hasEmployees = jobResources.some(
            (r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Employee && resourceHasAnyAssignments(r),
        );
        const employeeRequirementMet = !hasAssignedResources || updatedJob.isQuickRental || hasEmployees;

        setValues({
            ...updatedJob,
            hasResources: resourcesSet ? 'true' : '',
            hasEmployeeOrQuickRental: employeeRequirementMet ? 'true' : '',
        });
    }, [setValues, job.startTime]);

    if (resourceKitsLoading || resourcesLoading || contractLoading) {
        return <FormCircularProgress />;
    }

    return (
        <>
            <ResourcePanel
                job={{
                    resources,
                    ...job,
                }}
                contract={contract}
                selectedDate={new Date(job.startTime)}
                edit={{
                    allowResources: true,
                    resourceErrors: {},
                }}
                onJobChange={handleResourceUpdate}
                selectedResourceKits={job.resourceKits}
                resourceKits={resourceKits}
                isRentalContract={isRentalContract}
                errors={{}}
            />

            <Input
                name="hasResources"
                hidden
                required
                error="One or more resources must be assigned to the date selected"
            />

            {!job.isQuickRental && (
                <Input
                    name="hasEmployeeOrQuickRental"
                    hidden
                    required
                    error="At least one employee resource is required."
                />
            )}
        </>
    );
};
